import { useState, useEffect } from 'react';
import { useTheme } from '../theme';
import { db } from '../dbClient';
import { useToast } from '../Toast';

const SCENARIO_TEMPLATES = [
  { code: 'PEP_STATUS', name: 'PEP Detection', description: 'Subject is a Politically Exposed Person' },
  { code: 'HIGH_VALUE_TXN', name: 'High-Value Transaction', description: 'Transaction exceeds threshold' },
  { code: 'HIGH_RISK_JURISDICTION', name: 'High-Risk Jurisdiction', description: 'Activity in FATF gray/blacklist' },
  { code: 'COMPLEX_OWNERSHIP', name: 'Complex Ownership', description: 'Multiple layers of ownership' },
  { code: 'SANCTIONS_ADJACENT', name: 'Sanctions Adjacent', description: 'Close to sanctioned entity' },
  { code: 'UNUSUAL_PATTERN', name: 'Unusual Pattern', description: 'Deviation from normal activity' },
];

export default function RiskConfiguration({ company }) {
  const C = useTheme();
  const { showToast } = useToast();
  const [view, setView] = useState('scenarios'); // scenarios, matrices
  const [scenarios, setScenarios] = useState([]);
  const [matrices, setMatrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScenarioForm, setShowScenarioForm] = useState(false);
  const [newScenario, setNewScenario] = useState({
    code: '',
    name: '',
    description: '',
    risk_level: 'HIGH',
    auto_escalate: false,
    require_review: true,
  });

  useEffect(() => {
    if (company?.id) {
      loadScenarios();
      loadMatrices();
    }
  }, [company?.id]);

  async function loadScenarios() {
    if (!company?.id) return;
    try {
      setLoading(true);
      const { data, error } = await db
        .from('scenario_types')
        .select('*')
        .eq('company_id', company.id)
        .order('name', { ascending: true });

      if (error) throw error;
      setScenarios(data || []);
    } catch (err) {
      showToast('Error loading scenarios', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function loadMatrices() {
    if (!company?.id) return;
    try {
      const { data, error } = await db
        .from('risk_matrices')
        .select('*')
        .eq('company_id', company.id)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setMatrices(data || []);
    } catch (err) {
      console.error('Error loading risk matrices:', err);
    }
  }

  async function handleAddScenario() {
    if (!newScenario.code || !newScenario.name) {
      showToast('Code and name are required', 'error');
      return;
    }

    try {
      const { error } = await db
        .from('scenario_types')
        .insert([{
          company_id: company.id,
          code: newScenario.code.toUpperCase(),
          name: newScenario.name,
          description: newScenario.description,
          risk_level: newScenario.risk_level,
          auto_escalate: newScenario.auto_escalate,
          require_review: newScenario.require_review,
        }]);

      if (error) throw error;
      showToast('Scenario created', 'success');
      setNewScenario({
        code: '',
        name: '',
        description: '',
        risk_level: 'HIGH',
        auto_escalate: false,
        require_review: true,
      });
      setShowScenarioForm(false);
      loadScenarios();
    } catch (err) {
      showToast('Error creating scenario', 'error');
      console.error(err);
    }
  }

  async function handleDeleteScenario(id) {
    if (!confirm('Delete this scenario type?')) return;
    try {
      const { error } = await db
        .from('scenario_types')
        .delete()
        .eq('id', id);

      if (error) throw error;
      showToast('Scenario deleted', 'success');
      loadScenarios();
    } catch (err) {
      showToast('Error deleting scenario', 'error');
      console.error(err);
    }
  }

  function useTemplate(template) {
    setNewScenario({
      code: template.code,
      name: template.name,
      description: template.description,
      risk_level: 'HIGH',
      auto_escalate: false,
      require_review: true,
    });
    setShowScenarioForm(true);
  }

  const getRiskColor = (level) => {
    const colors = { CRITICAL: '#FF6B6B', HIGH: '#FF9800', MEDIUM: '#FFA500', LOW: '#4CAF50' };
    return colors[level] || C.textMid;
  };

  if (loading) {
    return <div style={{ padding: '2rem', color: C.textDim }}>Loading risk configuration...</div>;
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: C.text, marginBottom: '0.5rem', fontFamily: "'Syne', sans-serif" }}>
          Risk Assessment & Scenarios
        </h2>
        <p style={{ color: C.textDim, fontSize: '0.9rem' }}>
          Define risk levels, scenario types, and custom assessment matrices for your organization.
        </p>
      </div>

      {/* View tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem' }}>
        {[
          { value: 'scenarios', label: `🎯 Scenarios (${scenarios.length})` },
          { value: 'matrices', label: `📊 Risk Matrices (${matrices.length})` },
        ].map(v => (
          <button
            key={v.value}
            onClick={() => setView(v.value)}
            style={{
              padding: '0.6rem 1rem',
              background: view === v.value ? C.accent : 'transparent',
              color: view === v.value ? '#0A0E1A' : C.text,
              border: `1px solid ${view === v.value ? C.accent : C.border}`,
              borderRadius: 6,
              cursor: 'pointer',
              fontFamily: "'DM Mono', monospace",
              fontWeight: view === v.value ? 700 : 400,
              fontSize: '0.85rem'
            }}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Scenarios View */}
      {view === 'scenarios' && (
        <div>
          {!showScenarioForm && (
            <button
              onClick={() => setShowScenarioForm(true)}
              style={{
                padding: '0.6rem 1.2rem',
                background: `${C.accent}22`,
                color: C.accent,
                border: `1px solid ${C.accent}`,
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                fontWeight: 700,
                fontSize: '0.85rem',
                marginBottom: '2rem'
              }}
            >
              + Create Scenario
            </button>
          )}

          {showScenarioForm && (
            <div style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h3 style={{ color: C.text, marginBottom: '1rem', fontFamily: "'Syne', sans-serif" }}>
                Create Risk Scenario
              </h3>

              <div style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                      Code (e.g., PEP_STATUS) *
                    </label>
                    <input
                      type="text"
                      value={newScenario.code}
                      onChange={(e) => setNewScenario({...newScenario, code: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: C.bg,
                        border: `1px solid ${C.border}`,
                        borderRadius: 6,
                        color: C.text,
                        fontFamily: "'DM Mono', monospace",
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                      Display Name *
                    </label>
                    <input
                      type="text"
                      value={newScenario.name}
                      onChange={(e) => setNewScenario({...newScenario, name: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: C.bg,
                        border: `1px solid ${C.border}`,
                        borderRadius: 6,
                        color: C.text,
                        fontFamily: "'DM Mono', monospace",
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                    Description
                  </label>
                  <textarea
                    value={newScenario.description}
                    onChange={(e) => setNewScenario({...newScenario, description: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      background: C.bg,
                      border: `1px solid ${C.border}`,
                      borderRadius: 6,
                      color: C.text,
                      fontFamily: "'DM Mono', monospace",
                      fontSize: '0.9rem',
                      minHeight: '80px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div>
                  <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                    Risk Level
                  </label>
                  <select
                    value={newScenario.risk_level}
                    onChange={(e) => setNewScenario({...newScenario, risk_level: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      background: C.bg,
                      border: `1px solid ${C.border}`,
                      borderRadius: 6,
                      color: C.text,
                      fontFamily: "'DM Mono', monospace",
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="CRITICAL">Critical</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{ color: C.text, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newScenario.auto_escalate}
                      onChange={(e) => setNewScenario({...newScenario, auto_escalate: e.target.checked})}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.9rem' }}>Auto-escalate</span>
                  </label>
                  <label style={{ color: C.text, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newScenario.require_review}
                      onChange={(e) => setNewScenario({...newScenario, require_review: e.target.checked})}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.9rem' }}>Require Review</span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={handleAddScenario}
                  style={{
                    padding: '0.6rem 1.2rem',
                    background: C.accent,
                    color: '#0A0E1A',
                    border: 'none',
                    borderRadius: 6,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: "'DM Mono', monospace",
                    fontSize: '0.85rem'
                  }}
                >
                  Create Scenario
                </button>
                <button
                  onClick={() => setShowScenarioForm(false)}
                  style={{
                    padding: '0.6rem 1.2rem',
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                    color: C.textMid,
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontFamily: "'DM Mono', monospace",
                    fontSize: '0.85rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Templates */}
          {!showScenarioForm && scenarios.length === 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ color: C.text, marginBottom: '1rem', fontFamily: "'Syne', sans-serif", fontSize: '0.95rem' }}>
                Scenario Templates
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                {SCENARIO_TEMPLATES.map(template => (
                  <div
                    key={template.code}
                    style={{
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      padding: '1rem'
                    }}
                  >
                    <div style={{ color: C.text, fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                      {template.name}
                    </div>
                    <div style={{ color: C.textDim, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                      {template.description}
                    </div>
                    <button
                      onClick={() => useTemplate(template)}
                      style={{
                        padding: '0.4rem 0.8rem',
                        background: `${C.accent}22`,
                        color: C.accent,
                        border: `1px solid ${C.accent}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontFamily: "'DM Mono', monospace",
                        fontWeight: 600,
                        width: '100%'
                      }}
                    >
                      Use Template
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scenarios List */}
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {scenarios.length === 0 && showScenarioForm ? null : scenarios.length === 0 ? (
              <div style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: '2rem',
                textAlign: 'center',
                color: C.textDim
              }}>
                No scenarios configured. Start with a template or create a custom one.
              </div>
            ) : (
              scenarios.map(scenario => (
                <div
                  key={scenario.id}
                  style={{
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>
                      {scenario.name}
                      <span style={{
                        background: getRiskColor(scenario.risk_level) + '22',
                        color: getRiskColor(scenario.risk_level),
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        padding: '0.2rem 0.5rem',
                        borderRadius: 3,
                        marginLeft: '0.5rem'
                      }}>
                        {scenario.risk_level}
                      </span>
                    </div>
                    <div style={{ color: C.textDim, fontSize: '0.75rem', marginTop: '0.5rem' }}>
                      Code: {scenario.code}
                      {scenario.description && <div style={{ marginTop: '0.25rem' }}>{scenario.description}</div>}
                    </div>
                    <div style={{ color: C.textDim, fontSize: '0.7rem', marginTop: '0.5rem' }}>
                      {scenario.auto_escalate && <span>⚡ Auto-escalate</span>}
                      {scenario.require_review && <span style={{ marginLeft: '0.5rem' }}>👁️ Requires Review</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteScenario(scenario.id)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: 'transparent',
                      border: `1px solid #FF6B6B66`,
                      color: '#FF6B6B',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontFamily: "'DM Mono', monospace",
                      fontWeight: 600
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Matrices View */}
      {view === 'matrices' && (
        <div style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: '2rem',
          textAlign: 'center',
          color: C.textDim
        }}>
          Risk Matrix builder coming soon. Define custom CRR and TRA matrices here.
        </div>
      )}
    </div>
  );
}
